import { fasPlay } from 'shared/@generated/icons/fasPlay';
import { Pressable, View } from 'react-native';
import { useEffect, useRef, useState } from 'react';
import type Hls from 'hls.js';
import { VideoProps } from './index';
import { Icon } from 'shared/components/icon';
import { useInViewOnce } from 'shared/library/use-in-view';
import {
  hlsPlaybackConfig,
  isBufferedVideoEnd,
  isHlsVideoUrl,
} from 'shared/library/video-playback';

export const Video = function ({
  src,
  fallbackSrc,
  onProgress,
  onEnded,
  containerClassName,
  videoClassName,
  controls = true,
  autoPlay = false,
  muted = true,
  loop = false,
  poster,
  priority = false,
}: VideoProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(autoPlay);
  const [hasEnteredViewport, setHasEnteredViewport] = useState(false);
  const containerRef = useInViewOnce(() => setHasEnteredViewport(true), { threshold: 0 });

  const videoSrc = src || null;
  const isHls = !!videoSrc && isHlsVideoUrl(videoSrc);
  const shouldLoadHls = priority || autoPlay || hasEnteredViewport;

  useEffect(() => {
    const video = videoRef.current;
    setIsPlaying(autoPlay);
    if (!video || !videoSrc || !isHls) return;
    let cancelled = false;
    let loadStarted = false;
    let usingFallback = false;
    let playRequested = autoPlay || !video.paused;
    let hls: Hls | undefined;
    let endStallTimer: ReturnType<typeof setTimeout> | undefined;
    let resumeAfterFallback: (() => void) | undefined;
    const clearEndStall = () => {
      clearTimeout(endStallTimer);
      endStallTimer = undefined;
    };
    const fallback = () => {
      if (cancelled || usingFallback) return;
      usingFallback = true;
      clearEndStall();
      const resumeTime = video.currentTime;
      const shouldResume = playRequested;
      hls?.destroy();
      hls = undefined;
      if (!fallbackSrc) return;
      resumeAfterFallback = () => {
        if (!cancelled && Number.isFinite(video.duration)) {
          video.currentTime = Math.min(resumeTime, video.duration);
        }
      };
      video.addEventListener('loadedmetadata', resumeAfterFallback, { once: true });
      video.src = fallbackSrc;
      if (shouldResume) void video.play().catch(() => setIsPlaying(!video.paused));
    };
    const load = async () => {
      if (cancelled || loadStarted || usingFallback) return;
      loadStarted = true;
      try {
        const { default: HlsPlayer } = await import('hls.js');
        if (cancelled || usingFallback) return;
        if (!HlsPlayer.isSupported()) {
          if (video.canPlayType('application/vnd.apple.mpegurl')) {
            if (video.src !== videoSrc) video.src = videoSrc;
            if (playRequested) void video.play().catch(() => setIsPlaying(!video.paused));
          } else fallback();
          return;
        }
        hls = new HlsPlayer(hlsPlaybackConfig);
        hls.on(HlsPlayer.Events.MEDIA_ATTACHED, () => {
          if (!playRequested) return;
          hls?.startLoad();
          void video.play().catch(() => setIsPlaying(!video.paused));
        });
        hls.on(HlsPlayer.Events.ERROR, (_event, data) => {
          if (data.fatal) fallback();
          else if (
            fallbackSrc &&
            data.details === HlsPlayer.ErrorDetails.BUFFER_STALLED_ERROR &&
            !endStallTimer &&
            hls?.levels[hls.currentLevel]?.details?.live === false
          ) {
            const bufferedEnd = video.buffered.length
              ? video.buffered.end(video.buffered.length - 1)
              : 0;
            const stalledTime = video.currentTime;
            if (!isBufferedVideoEnd(stalledTime, video.duration, bufferedEnd)) return;
            // Recover a persistent VOD AAC-tail stall with real MP4 playback/end events.
            endStallTimer = setTimeout(() => {
              endStallTimer = undefined;
              if (
                !cancelled &&
                !video.paused &&
                !video.ended &&
                video.readyState < 3 &&
                Math.abs(video.currentTime - stalledTime) < 0.01
              ) {
                fallback();
              }
            }, 1500);
          }
        });
        hls.loadSource(videoSrc);
        if (playRequested) hls.attachMedia(video);
      } catch {
        fallback();
      }
    };
    const start = () => {
      playRequested = true;
      if (usingFallback) return;
      if (hls?.media === video) return;
      if (hls) hls.attachMedia(video);
      else void load();
    };
    const pause = () => {
      playRequested = false;
      clearEndStall();
    };
    // An empty MediaSource looks like endless buffering in native browser controls.
    // Keep a preload="none" URL until play; only then attach HLS and fetch segments.
    if (!autoPlay) video.src = videoSrc;
    video.addEventListener('play', start);
    video.addEventListener('pause', pause);
    video.addEventListener('error', fallback);
    // An idle offscreen video has no manifest request or HLS module download.
    if (shouldLoadHls) void load();
    return () => {
      cancelled = true;
      clearEndStall();
      video.removeEventListener('play', start);
      video.removeEventListener('pause', pause);
      video.removeEventListener('error', fallback);
      if (resumeAfterFallback) video.removeEventListener('loadedmetadata', resumeAfterFallback);
      hls?.destroy();
      if (!video.isConnected) {
        video.pause();
        video.removeAttribute('src');
        video.load();
      }
    };
  }, [autoPlay, fallbackSrc, isHls, shouldLoadHls, videoSrc]);
  const effectiveMuted = muted || !controls;

  const togglePlayPause = () => {
    const video = videoRef.current;
    if (!video) return;

    if (video.paused) {
      void video
        .play()
        .then(() => setIsPlaying(true))
        .catch(() => setIsPlaying(!video.paused));
    } else {
      video.pause();
      setIsPlaying(false);
    }
  };

  if (!videoSrc) {
    return <View ref={containerRef} className={`${containerClassName} bg-brand-cream relative`} />;
  }

  return (
    <View ref={containerRef} className={`${containerClassName} relative`}>
      <video
        key={videoSrc}
        ref={videoRef}
        src={isHls ? undefined : videoSrc}
        preload={autoPlay ? 'auto' : 'none'}
        className={`absolute inset-0 h-full w-full ${videoClassName ?? ''}`}
        controls={controls}
        autoPlay={autoPlay}
        muted={effectiveMuted}
        loop={loop}
        // A poster on a hidden slide otherwise downloads eagerly, even with preload="none".
        poster={priority || autoPlay || hasEnteredViewport ? poster : undefined}
        playsInline
        onPlay={() => setIsPlaying(true)}
        onPlaying={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
        onEnded={onEnded}
        onTimeUpdate={(event) =>
          onProgress?.(event.currentTarget.currentTime, event.currentTarget.duration)
        }
      />

      {!controls && (
        <Pressable
          className="absolute inset-0 items-center justify-center"
          onPress={togglePlayPause}
        >
          {!isPlaying && (
            <View className="size-16 items-center justify-center rounded-full border-2 border-white bg-black/30">
              <Icon icon={fasPlay} className="ml-1 size-8 text-white" />
            </View>
          )}
        </Pressable>
      )}
    </View>
  );
};
